import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, Divider, FormControl, InputLabel,
  MenuItem, Select, Skeleton, Stack, Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import ModuleLayout from "../../components/enterprise/ModuleLayout";
import EmptyState from "../../components/dashboard/EmptyState";
import { monitoringTabs } from "../navigation";
import saasApi from "../../utils/saasApi";
import { useSharedFilters, forEndpoint } from "../../hooks/useSharedFilters";

const statuses = ["online", "offline", "degraded"];
const severityColors = { CRITICAL: "error", HIGH: "warning", MEDIUM: "info", LOW: "default" };

const OperationsCenter = () => {
  const { t, i18n } = useTranslation();
  const { filters, setFilter: changeFilter, pathFor } = useSharedFilters("operations");
  const days = Number(filters.days);
  const branchId = filters.branch_id;
  const [refreshKey, setRefreshKey] = useState(0);
  const [summary, setSummary] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [cameraLoading, setCameraLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [cameraError, setCameraError] = useState("");

  useEffect(() => {
    let active = true;
    setSummaryLoading(true);
    setSummaryError("");
    saasApi.getExecutiveSummary(forEndpoint({ days: String(days), branch_id: branchId }, "operations"))
      .then((value) => { if (active) setSummary(value); })
      .catch(() => { if (active) { setSummary(null); setSummaryError(t("operations.summaryError")); } })
      .finally(() => { if (active) setSummaryLoading(false); });
    return () => { active = false; };
  }, [days, branchId, refreshKey, t]);

  useEffect(() => {
    let active = true;
    setCameraLoading(true);
    setCameraError("");
    saasApi.getCameras()
      .then((value) => { if (active) setCameras(value.cameras || []); })
      .catch(() => { if (active) { setCameras([]); setCameraError(t("operations.cameraError")); } })
      .finally(() => { if (active) setCameraLoading(false); });
    return () => { active = false; };
  }, [refreshKey, t]);

  const filteredCameras = useMemo(
    () => cameras.filter((camera) => !branchId || String(camera.branch_id) === branchId),
    [cameras, branchId]
  );
  const cameraCounts = useMemo(() => {
    const counts = { online: 0, offline: 0, degraded: 0, other: 0 };
    filteredCameras.forEach((camera) => {
      const status = String(camera.status || "").toLowerCase();
      counts[statuses.includes(status) ? status : "other"] += 1;
    });
    return counts;
  }, [filteredCameras]);
  const branches = summary?.branches || [];

  const incidentsUrl = (extra = {}) => {
    return pathFor("/incidents", extra, summary?.period);
  };

  return (
    <ModuleLayout
      title={t("operations.title")}
      subtitle={t("operations.subtitle")}
      breadcrumbs={[t("platform.nav.monitoring"), t("operations.title")]}
      tabs={monitoringTabs}
      actions={<Stack direction="row" gap={1} flexWrap="wrap"><Button component={RouterLink} to={pathFor("/overview")}>{t("executive.title")}</Button><Button startIcon={<RefreshIcon />} onClick={() => setRefreshKey((value) => value + 1)}>{t("executive.refresh")}</Button></Stack>}
    >
      <Stack spacing={3} sx={{ pb: 4 }}>
        <Alert severity="info" variant="outlined">{t("operations.recordedNotice")}</Alert>
        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel id="operations-period-label">{t("executive.period")}</InputLabel>
            <Select labelId="operations-period-label" label={t("executive.period")} value={days} onChange={(event) => changeFilter("days", String(event.target.value))}>
              {[7, 30, 90].map((value) => <MenuItem key={value} value={value}>{t(`executive.last${value}Days`)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel id="operations-branch-label">{t("executive.branch")}</InputLabel>
            <Select labelId="operations-branch-label" label={t("executive.branch")} value={branchId} onChange={(event) => changeFilter("branch_id", event.target.value)}>
              <MenuItem value="">{t("executive.allBranches")}</MenuItem>
              {branches.map((branch) => <MenuItem key={branch.id} value={String(branch.id)}>{branch.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        <Box component="section" aria-label={t("operations.cameraSection")}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>{t("operations.cameraSection")}</Typography>
              <Typography variant="body2" color="text.secondary">{t("operations.cameraSource")}</Typography>
            </Box>
            <Button component={RouterLink} to="/monitoring/cameras" endIcon={<ArrowForwardIcon />}>{t("operations.viewCameras")}</Button>
          </Stack>
          {cameraError && <Alert severity="error" sx={{ mb: 1 }}>{cameraError}</Alert>}
          {cameraLoading ? <Skeleton variant="rectangular" height={116} /> : !cameraError && (
            <>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                <Chip icon={<CameraAltOutlinedIcon />} label={`${t("operations.registered")}: ${filteredCameras.length}`} />
                {statuses.map((status) => <Chip key={status} variant="outlined" color={status === "online" ? "success" : status === "offline" ? "error" : "warning"} label={`${t(`operations.${status}`)}: ${cameraCounts[status]}`} />)}
                {cameraCounts.other > 0 && <Chip variant="outlined" label={`${t("operations.otherStatus")}: ${cameraCounts.other}`} />}
              </Stack>
              {filteredCameras.length === 0 ? <EmptyState compact title={t("operations.noCameras")} /> : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
                  {filteredCameras.slice(0, 8).map((camera) => (
                    <Stack key={camera.id} direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ minWidth: 0, px: 1.5, py: 1.2, border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
                      <Box minWidth={0}>
                        <Typography variant="body2" fontWeight={700} noWrap>{camera.display_name || camera.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{camera.branch_name || t("operations.noBranch")}</Typography>
                      </Box>
                      <Chip size="small" variant="outlined" label={t(`operations.${statuses.includes(String(camera.status || "").toLowerCase()) ? String(camera.status).toLowerCase() : "otherStatus"}`)} />
                    </Stack>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        <Divider />
        <Box component="section" aria-label={t("operations.alertSection")}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>{t("operations.alertSection")}</Typography>
              <Typography variant="body2" color="text.secondary">{summary?.period?.starts_on && summary?.period?.ends_on ? t("executive.source", { from: summary.period.starts_on, to: summary.period.ends_on, timezone: summary.period.session_time_zone || "-" }) : t("operations.periodSource")}</Typography>
            </Box>
            <Button component={RouterLink} to={incidentsUrl()} endIcon={<ArrowForwardIcon />}>{t("executive.viewAll")}</Button>
          </Stack>
          {summaryError && <Alert severity="error" sx={{ mb: 1 }}>{summaryError}</Alert>}
          {summaryLoading ? <Skeleton variant="rectangular" height={160} /> : !summaryError && summary && (
            <>
              <Stack direction={{ xs: "column", md: "row" }} gap={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">{t("executive.alerts")}</Typography>
                  <Typography variant="h3" sx={{ fontVariantNumeric: "tabular-nums" }}>{summary.metrics?.alerts == null ? t("platform.metrics.unavailableShort") : summary.metrics.alerts}</Typography>
                </Box>
                <Box sx={{ flex: 2, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">{t("operations.severitySection")}</Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    {(summary.severity_distribution || []).map((row) => <Chip key={row.severity} component={RouterLink} clickable to={incidentsUrl({ severity: row.severity })} color={severityColors[row.severity] || "default"} variant="outlined" label={`${row.severity}: ${row.count}`} />)}
                    {!summary.severity_distribution?.length && <Typography variant="body2" color="text.secondary">{t("operations.noAlerts")}</Typography>}
                  </Stack>
                </Box>
              </Stack>
              {!summary.recent_alerts?.length ? <EmptyState compact title={t("operations.noAlerts")} /> : (
                <Stack divider={<Divider flexItem />}>
                  {summary.recent_alerts.map((alert) => (
                    <Stack key={alert.id} direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={1} sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                        <NotificationsActiveOutlinedIcon fontSize="small" color="action" />
                        <Box minWidth={0}>
                          <Typography variant="body2" fontWeight={650} noWrap>{alert.camera_name || t("operations.unknownCamera")}</Typography>
                          <Typography variant="caption" color="text.secondary">{alert.detection_type || t("operations.unknownDetection")} | {alert.occurred_at ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }).format(new Date(alert.occurred_at)) : "-"}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Chip size="small" variant="outlined" color={severityColors[String(alert.severity || "").toUpperCase()] || "default"} label={alert.severity || "UNKNOWN"} />
                        <Button size="small" component={RouterLink} to={incidentsUrl({ alert_id: alert.id })}>{t("operations.openAlert")}</Button>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Box>

        <Divider />
        <Box component="section" aria-label={t("operations.branchSection")}>
          <Typography variant="h5" fontWeight={700}>{t("operations.branchSection")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t("operations.branchSource")}</Typography>
          {summaryLoading ? <Skeleton variant="rectangular" height={100} /> : !summaryError && (
            branches.length ? <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
              {branches.map((branch) => <Stack key={branch.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <Stack direction="row" alignItems="center" gap={1} minWidth={0}><StorefrontOutlinedIcon fontSize="small" color="action" /><Typography variant="body2" noWrap>{branch.name}</Typography></Stack>
                <Button size="small" component={RouterLink} to={incidentsUrl({ branch_id: branch.id })}>{branch.alert_count}</Button>
              </Stack>)}
            </Box> : <EmptyState compact title={t("operations.noBranches")} />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">{t("operations.aiUnavailable")}</Typography>
      </Stack>
    </ModuleLayout>
  );
};

export default OperationsCenter;
